import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import PasswordField from '@components/Admin/Users/Forms/Fields/PasswordField';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
} from '@mui/material';
import { EditUserFormValues } from '@src/hooks/api/mutations/useUserUpdate';
import { User } from '@src/types/User';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import EmailField from './Fields/EmailField';
import FirstNameField from './Fields/FirstNameField';
import GroupField from './Fields/GroupField';
import LastNameField from './Fields/LastNameField';
import MfaRequiredField from './Fields/MfaRequiredField';
import ResetPasswordField from './Fields/ResetPasswordField';

const schema = yup
  .object({
    email: yup.string().email().label('Email'),
    firstName: yup.string().optional().label('First Name'),
    lastName: yup.string().optional().label('Last Name'),
    totp: yup.bool(),
    password: yup.string().optional().label('Password'),
    groups: yup
      .array()
      .of(yup.string().required())
      .min(1, 'Groups cannot be an empty array')
      .required()
      .label('Groups'),
  })
  .required();

const getDefaultValues = (user: User): EditUserFormValues => ({
  email: user?.email ?? '',
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  totp: user.totp,
  password: '',
  groups: [],
});

function EditUserForm({
  onCancel,
  onSubmit,
  isLoading,
  user,
}: {
  onCancel: () => void;
  onSubmit: (values: EditUserFormValues) => void;
  isLoading: boolean;
  user: User;
}) {
  const alias = useAlias();
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);

  const methods = useForm<EditUserFormValues>({
    defaultValues: getDefaultValues(user),
    resolver: yupResolver<EditUserFormValues>(schema),
  });

  const { handleSubmit } = methods;

  const displayPasswordField = (): React.JSX.Element | string =>
    showPasswordField ? <PasswordField /> : '';
  const onResetCheckboxChanged = (resetPasswordChecked: boolean): void => {
    setShowPasswordField(resetPasswordChecked);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack gap={2}>
            <TextField
              fullWidth
              label={alias('Username')}
              value={user.username}
              disabled
              size='small'
            />
            <EmailField />
            <FirstNameField />
            <LastNameField />
            <GroupField userId={user.id} />
            <MfaRequiredField totp={user.totp as boolean} />
            <ResetPasswordField
              onResetCheckboxChanged={onResetCheckboxChanged}
            />
            {displayPasswordField()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>
            <FormattedAlias id='Cancel' />
          </Button>
          <LoadingButton type='submit' variant='contained' loading={isLoading}>
            <FormattedAlias id='Save' />
          </LoadingButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

export default EditUserForm;
